/**
 * H5 Native Interaction bridge utilities.
 *
 * Detects whether the portal is running inside a native app webview (iOS/Android)
 * and provides a wrapper to invoke the JSCallBrowser protocol for opening URLs
 * in the device's default browser.
 */

/**
 * Checks if the native bridge is available (i.e., running inside a native app webview).
 * - Android: `window.nativeInterface` is injected by the native shell.
 * - iOS: `window.webkit.messageHandlers.nativeInterface` is injected by WKWebView.
 */
export const hasNativeBridge = (): boolean => {
  try {
    const hasAndroidBridge = typeof window.nativeInterface?.postMessage === "function";
    const hasIOSBridge = typeof window.webkit?.messageHandlers?.nativeInterface?.postMessage === "function";
    return hasAndroidBridge || hasIOSBridge;
  } catch {
    return false;
  }
};

interface JSCallBrowserPayload {
  parameter: {
    type: number;
    url: string;
    extraData: string;
  };
  funcName: string;
  className: string;
  callBack: string;
  callBackId: string;
}

/**
 * Opens a URL in the device's default browser via the H5 native bridge.
 * Uses JSCallBrowser with type: 1 to open in external browser (Safari/Chrome).
 *
 * @param url - The URL to open in the device's native browser
 * @returns true if the bridge was invoked successfully, false otherwise
 */
export const openInNativeBrowser = (url: string): boolean => {
  const payload: JSCallBrowserPayload = {
    parameter: {
      type: 1,
      url,
      extraData: "",
    },
    funcName: "JSCallBrowser",
    className: "JSBusinessRequest",
    callBack: "",
    callBackId: "",
  };

  const message = JSON.stringify(payload);

  try {
    if (typeof window.nativeInterface?.postMessage === "function") {
      window.nativeInterface.postMessage(message);
      return true;
    }

    if (typeof window.webkit?.messageHandlers?.nativeInterface?.postMessage === "function") {
      window.webkit.messageHandlers.nativeInterface.postMessage(message);
      return true;
    }
  } catch {
    // Fall through to return false — caller should use fallback behavior
  }

  return false;
};
